import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { PushProcessingNotification, splitWordsOnUpperCase } from "@repo/helpers";
import { FilterItem, KeyedArray, keyPair } from "@repo/models";
import { useCallback, useMemo } from "react";

import { useRepOnboardingQuery, useTabCountQuery } from "../../../api/repApi";
import { useRepDisciplineApi, useRepDisciplineQueries } from "../../../api/repDisciplineApi";
import { useRepSegmentsApi } from "../../../api/repSegmentsApi";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import { useRepContext, useRepDisciplineContext } from "../../../contexts/editableItemContext";
import { useSettingsQuery } from "../../../helpers/queryHooks";
import { RepDiscipline } from "../../../models";
import { tabs } from "../../../string";

interface IState extends ITemplateData {
	repDiscipline: RepDiscipline;
}

const TYPES = [
	{ id: 1, value: "Probation" },
	{ id: 2, value: "Suspension" },
	{ id: 3, value: "Blocklist" },
	{ id: 4, value: "Feature Suspension" },
];
const REASONS = [
	{ id: 1, value: "Complaint" },
	{ id: 2, value: "Onboarding" },
	{ id: 3, value: "Internal Review" },
	{ id: 4, value: "Alert" },
];

function useRepDisciplinesLists() {
	const [repSettings] = useSettingsQuery();
	const { repId } = useRepContext();
	const { useOnboardingSegmentsQuery } = useRepSegmentsApi(repId);
	const { useRepFeaturesQuery, useTrainingsQuery } = useRepDisciplineQueries();
	const { onboardingSegments, loaded: onboardingLoaded } = useOnboardingSegmentsQuery();
	const { data: repFeatures, loaded: repFeaturesLoaded } = useRepFeaturesQuery();
	const { data: trainings, loaded: trainingsLoaded } = useTrainingsQuery(repSettings?.mindflashEnabled);

	const allLoaded = trainingsLoaded && repFeaturesLoaded && onboardingLoaded;

	const activeLists = useMemo(() => {
		let filteredTypes = TYPES;

		if (repFeatures?.length === 0) filteredTypes = TYPES.filter((t) => t.id !== 4);

		return {
			types: filteredTypes,
			reasons: REASONS,
			features: repFeatures?.map((f) => ({
				id: f.id,
				value: splitWordsOnUpperCase(f.name),
			})),
			trainings: getItems(trainings as KeyedArray<FilterItem, number>, trainingsLoaded),
			hasTrainings: trainings?.any(),
			repSegments: onboardingSegments,
		};
	}, [trainings, repFeatures, onboardingSegments]);

	return [activeLists, allLoaded] as const;

	function getItems(allItems: KeyedArray<FilterItem, number>, loaded: boolean, forceInclude?: FilterItem) {
		if (!allItems) return [] as keyPair[];
		if (forceInclude) allItems.set(forceInclude);
		return allItems.map((c) => {
			return { id: c.id, value: c.name } as keyPair;
		});
	}
}

interface Props {
	postSave?: () => Promise<void>;
}

function RepDisciplineEditorDynamic(props: Props) {
	const { repId } = useRepContext();
	const context = useRepDisciplineContext();
	const { saveRepDiscipline, loadRepDiscipline } = useRepDisciplineApi();
	const { useRepDisciplineQuery } = useRepDisciplineQueries();
	const { clearQuery } = useRepDisciplineQuery();
	const repDiscipline = context.item;
	const [repDisciplinesLists, listsLoaded] = useRepDisciplinesLists();
	const { clearQuery: clearTabCount } = useTabCountQuery(repId);
	const { checkOnboarding } = useRepOnboardingQuery();

	const templateData = useMemo(
		() => ({
			repDiscipline,
			repDisciplinesLists,
		}),
		[repDiscipline, repDisciplinesLists],
	);

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Discipline...");
		try {
			const response = await saveRepDiscipline(repDiscipline);
			notify.processing("Loading...");
			const newDiscipline = await loadRepDiscipline(response);
			notify.processing("Checking Onboarding Status...");
			await checkOnboarding();
			await clearQuery();
			await clearTabCount();
			notify.success("Saved!");

			return newDiscipline;
		} catch (ex) {
			notify.error("Failed to save");
			throw ex;
		}
	}, [repId, repDiscipline]);

	const updateValues = useMemo(() => updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		//Custom logic
		switch (id) {
			default:
				break;
		}

		return newState as IState;
	}

	return (
		<EditableItemDynamicTab
			itemContext={context}
			pageName={tabs.repDisciplines}
			otherTemplateData={templateData}
			itemProp="repDiscipline"
			updateValues={updateValues}
			save={save}
			postSave={props.postSave}
			loaded={listsLoaded}
		/>
	);
}

export default RepDisciplineEditorDynamic;
